// src/dashboard/pages/teacher/Objectifs/components/ObjectifDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaBullseye, FaSave, FaTimes } from "react-icons/fa";

import {
  clampProgress,
  getAdherentDisplayName,
  getObjectifAdherent,
  OBJECTIF_TYPES,
  OBJECTIF_TYPE_LABELS,
  toDateInputValue,
} from "../utils/objectifsDashboardUtils";

const initialForm = {
  adherent_id: "",
  saison: "",
  title: "",
  description: "",
  type: "",
  progress: 0,
  is_done: false,
  start_date: "",
  due_date: "",
};

export default function ObjectifDrawer({
  isOpen,
  mode = "create",
  objectif,
  adherents = [],
  filteredAdherents = [],
  seasonOptions = [],
  defaultSeason = "",
  onClose,
  onCreate,
  onUpdate,
  saving,
}) {
  const [formData, setFormData] = useState(initialForm);

  const isEdit = mode === "edit";

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && objectif) {
      setFormData({
        adherent_id: objectif.adherent_id
          ? String(objectif.adherent_id)
          : String(getObjectifAdherent(objectif)?.id || ""),
        saison: objectif.saison || defaultSeason || seasonOptions[1] || "",
        title: objectif.title || "",
        description: objectif.description || "",
        type: objectif.type || "",
        progress: clampProgress(objectif.progress),
        is_done: !!objectif.is_done,
        start_date: toDateInputValue(objectif.start_date),
        due_date: toDateInputValue(objectif.due_date),
      });
    } else {
      setFormData({
        ...initialForm,
        saison: defaultSeason || seasonOptions[1] || seasonOptions[0] || "",
      });
    }
  }, [isOpen, isEdit, objectif, defaultSeason, seasonOptions]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [isOpen, saving, onClose]);

  const adherentOptions = useMemo(() => {
    const map = new Map();

    filteredAdherents.forEach((adherent) => {
      map.set(String(adherent.id), adherent);
    });

    if (isEdit && objectif) {
      const currentAdherent = getObjectifAdherent(objectif);
      const currentId = objectif.adherent_id || currentAdherent?.id;

      if (currentId && currentAdherent) {
        map.set(String(currentId), {
          ...currentAdherent,
          id: currentId,
        });
      }
    }

    if (map.size === 0) {
      adherents.forEach((adherent) => {
        map.set(String(adherent.id), adherent);
      });
    }

    return [...map.values()].sort((a, b) =>
      getAdherentDisplayName(a).localeCompare(getAdherentDisplayName(b), "fr", {
        sensitivity: "base",
      })
    );
  }, [adherents, filteredAdherents, isEdit, objectif]);

  const canSubmit = useMemo(() => {
    return (
      String(formData.adherent_id || "").trim() &&
      String(formData.title || "").trim() &&
      String(formData.saison || "").trim() &&
      !saving
    );
  }, [formData, saving]);

  const updateField = (name, value) => {
    setFormData((prev) => {
      if (name === "progress") {
        const progress = clampProgress(value);

        return {
          ...prev,
          progress,
          is_done: progress >= 100 ? true : prev.is_done,
        };
      }

      if (name === "is_done") {
        const isDone = !!value;

        return {
          ...prev,
          is_done: isDone,
          progress: isDone ? 100 : prev.progress,
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    if (isEdit && objectif?.id) {
      await onUpdate?.(objectif.id, formData);
      return;
    }

    await onCreate?.(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              {isEdit ? "Modification" : "Nouvel objectif"}
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {isEdit ? "Modifier l’objectif" : "Créer un objectif"}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Renseignez l’adhérent, la saison, le type d’objectif, la
              progression et les dates éventuelles.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto p-5"
        >
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-[#013E8E] shadow-sm dark:bg-slate-900 dark:text-indigo-300">
                <FaBullseye />
              </span>

              <div>
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  Objectif pédagogique
                </p>
                <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                  L’adhérent, la saison et le titre sont obligatoires.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Adhérent
              </label>

              <select
                value={formData.adherent_id}
                onChange={(event) =>
                  updateField("adherent_id", event.target.value)
                }
                disabled={isEdit || saving}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                required
                autoFocus
              >
                <option value="">— Sélectionner un adhérent —</option>
                {adherentOptions.map((adherent) => (
                  <option key={adherent.id} value={adherent.id}>
                    {getAdherentDisplayName(adherent)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Saison
              </label>

              <select
                value={formData.saison}
                onChange={(event) => updateField("saison", event.target.value)}
                disabled={saving}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                required
              >
                {seasonOptions.map((season) => (
                  <option key={season} value={season}>
                    {season}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Titre
            </label>

            <input
              type="text"
              value={formData.title}
              onChange={(event) => updateField("title", event.target.value)}
              placeholder="Ex : Préparer la ceinture orange"
              disabled={saving}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Description
            </label>

            <textarea
              value={formData.description}
              onChange={(event) =>
                updateField("description", event.target.value)
              }
              rows={5}
              placeholder="Détails de l’objectif, points techniques à travailler, remarques..."
              disabled={saving}
              className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Type
              </label>

              <select
                value={formData.type}
                onChange={(event) => updateField("type", event.target.value)}
                disabled={saving}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                <option value="">— Aucun type —</option>
                {OBJECTIF_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {OBJECTIF_TYPE_LABELS[type] || type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Statut
              </label>

              <select
                value={formData.is_done ? "done" : "todo"}
                onChange={(event) =>
                  updateField("is_done", event.target.value === "done")
                }
                disabled={saving}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                <option value="todo">En cours</option>
                <option value="done">Terminé</option>
              </select>
            </div>
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between gap-3">
              <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Progression
              </label>

              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                {clampProgress(formData.progress)}%
              </span>
            </div>

            <input
              type="range"
              min="0"
              max="100"
              value={clampProgress(formData.progress)}
              onChange={(event) =>
                updateField("progress", Number(event.target.value))
              }
              disabled={saving}
              className="w-full accent-[#013E8E]"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Date de début
              </label>

              <input
                type="date"
                value={formData.start_date}
                onChange={(event) =>
                  updateField("start_date", event.target.value)
                }
                disabled={saving}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Échéance
              </label>

              <input
                type="date"
                value={formData.due_date}
                onChange={(event) =>
                  updateField("due_date", event.target.value)
                }
                disabled={saving}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>
          </div>
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />
              {saving
                ? "Enregistrement..."
                : isEdit
                ? "Enregistrer"
                : "Créer l’objectif"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}